import PropTypes from "prop-types";
import React from "react";

import {
  InputGroup,
  FormGroup,
  Label,
  InputGroupText,
  Row,
  Col,
} from "reactstrap";

import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import DatePickerRc from "react-datepicker";
import ValidationMessage from "src/Components/Form/ValidationMessage";
import hashInputId from "src/utils/jsHelpers/hashInputId";
import __ from "src/utils/Translations";
import { isDateValid } from "src/utils/jsHelpers/getDateAndTime";

export default function DataPicker({
  label,
  id,
  value,
  onChange,
  tooltip,
  format,
  errorMessage,
  validateField,
  validation,
  tooltipPosition,
  inputSwitcher,
  disabled,
  allowSameDay,
  showTimeSelect,
  minDate,
  confirmChange,
}) {
  const onValueChanged = (newValue) => {
    let updatedValue = newValue;
    if (newValue instanceof Date && !showTimeSelect && !allowSameDay) {
      updatedValue.setHours(0);
      updatedValue.setMinutes(0);
      updatedValue.setSeconds(0);
    }

    if (newValue instanceof Date && !showTimeSelect && allowSameDay) {
      updatedValue = new Date(
        newValue.getFullYear(),
        newValue.getMonth(),
        newValue.getDate(),
        23,
        59,
        59
      );
    }

    onChange(updatedValue);
  };

  const confirmationHandler = () => {
    if (value && typeof confirmChange === "function") {
      confirmChange();
    }

    validateField(id, value, validation);
  };

  const inputGroup = (
    <InputGroup>
      <InputGroupText>
        <FontAwesomeIcon icon={faCalendarAlt} />
      </InputGroupText>
      <div
        data-t1={id}
        className={`${
          showTimeSelect ? "datetime-picker-wrapper" : "date-picker-wrapper"
        }`}
      >
        <DatePickerRc
          id={hashInputId(id)}
          customInput={<input data-t1={id} type="text" />}
          selected={isDateValid(value) ? new Date(value) : null}
          selectsStart
          disabled={disabled}
          className={`form-control${errorMessage ? " is-invalid" : ""}`}
          invalid={Boolean(errorMessage)}
          onBlur={confirmationHandler}
          onCalendarClose={confirmationHandler}
          onChange={onValueChanged}
          popperPlacement="auto"
          showTimeSelect={showTimeSelect}
          dateFormat={
            showTimeSelect ? "dd/MM/yyyy H:mm" : format || "dd/MM/yyyy"
          }
          timeCaption={__("Godzina:")}
          minDate={minDate}
        />
      </div>
      {tooltip && tooltipPosition === "input" ? (
        <InputGroupText>{tooltip}</InputGroupText>
      ) : null}
      <ValidationMessage message={errorMessage} />
    </InputGroup>
  );

  return (
    <FormGroup>
      <Label data-t1={`${id}Label`} for={id} className="mr-sm-2">
        <span key={`label-text-${label}-${id}`}>{label}</span>
        {tooltip && tooltipPosition === "label" ? (
          <span key="tooltip"> {tooltip}</span>
        ) : null}
      </Label>
      {inputSwitcher ? (
        <Row>
          <Col sm={9}>{inputGroup}</Col>
          <Col sm={3}>{inputSwitcher}</Col>
        </Row>
      ) : (
        inputGroup
      )}
    </FormGroup>
  );
}

DataPicker.propTypes = {
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  format: PropTypes.string,
  showTimeSelect: PropTypes.bool,
  tooltip: PropTypes.node,
  inputSwitcher: PropTypes.node,
  disabled: PropTypes.bool,
  allowSameDay: PropTypes.bool,
  tooltipPosition: PropTypes.string,
  errorMessage: PropTypes.string,
  validateField: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  validation: PropTypes.array,
  minDate: PropTypes.instanceOf(Date),
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  confirmChange: PropTypes.func,
};

DataPicker.defaultProps = {
  id: "",
  label: "",
  format: "",
  showTimeSelect: false,
  tooltip: null,
  inputSwitcher: null,
  disabled: false,
  allowSameDay: false,
  tooltipPosition: "label",
  errorMessage: null,
  validateField: null,
  validation: null,
  minDate: undefined,
  value: PropTypes.instanceOf(Date),
  confirmChange: null,
};
